import { createSlice } from "@reduxjs/toolkit";
import {
    createNewRecord,
    getRecord,
    updateRecord,
    deleteRecord,
    checkActiveRecord,
    pauseRecord,
    resumeRecord,
    endRecord,
    updateRecordAndGetNextQuestion,
    createRecordAndGetFirstQuestion,
    resumeRecordAndGetQuestion,
    clearRecordError,
    getUserSectionStats,
    getMonthlySubjectAccuracy,
    getDailyAccuracyTrend
} from "../actions/recordActionsHelper";
import { recordsObject } from "../stateObject/recordsObject";

const initialState = recordsObject;

const recordSlice = createSlice({
    name: "record",
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getRecord.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getRecord.success, (state, action) => {
                state.recordData = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getRecord.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to fetch records.";
            })
            .addCase(createNewRecord.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createNewRecord.success, (state, action) => {
                state.recordData = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(createNewRecord.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Record creation failed. Please try again.";
            })
            .addCase(updateRecord.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateRecord.success, (state, action) => {
                state.recordData = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(updateRecord.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Record update failed. Please try again.";
            })
            .addCase(deleteRecord.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteRecord.success, (state, action) => {
                state.recordData = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(deleteRecord.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Record deletion failed. Please try again.";
            })
            .addCase(checkActiveRecord.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(checkActiveRecord.success, (state, action) => {
                state.recordData = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(checkActiveRecord.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to check active record.";
            })
            .addCase(pauseRecord.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(pauseRecord.success, (state, action) => {
                state.recordData = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(pauseRecord.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to pause record.";
            })
            .addCase(resumeRecord.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(resumeRecord.success, (state, action) => {
                state.recordData = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(resumeRecord.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to resume record.";
            })
            .addCase(endRecord.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(endRecord.success, (state, action) => {
                state.recordData = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(endRecord.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to end record.";
            })
            .addCase(updateRecordAndGetNextQuestion.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateRecordAndGetNextQuestion.success, (state, action) => {
                state.recordData = action.payload.updatedRecord; // Use the updated record from the response
                state.isLoading = false;
                state.error = null;
            })
            .addCase(updateRecordAndGetNextQuestion.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to update record and get next question.";
            })
            .addCase(createRecordAndGetFirstQuestion.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createRecordAndGetFirstQuestion.success, (state, action) => {
                state.recordData = action.payload.newRecord; // Use the new record from the response
                state.isLoading = false;
                state.error = null;
            })
            .addCase(createRecordAndGetFirstQuestion.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to create record and get first question.";
            })
            .addCase(resumeRecordAndGetQuestion.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(resumeRecordAndGetQuestion.success, (state, action) => {
                state.recordData = action.payload.resumedRecord; // Use the resumed record from the response
                state.isLoading = false;
                state.error = null;
            })
            .addCase(resumeRecordAndGetQuestion.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to resume record and get question.";
            })
            .addCase(clearRecordError, (state) => {
                state.error = null;
            })
            // User section statistics cases
            .addCase(getUserSectionStats.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getUserSectionStats.success, (state, action) => {
                state.userSectionStats = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getUserSectionStats.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to fetch user section statistics.";
            })
            // Monthly subject accuracy cases
            .addCase(getMonthlySubjectAccuracy.request, (state) => {
                state.isLoadingMonthlyAccuracy = true;
                state.error = null;
            })
            .addCase(getMonthlySubjectAccuracy.success, (state, action) => {
                state.monthlyAccuracyData = action.payload;
                state.isLoadingMonthlyAccuracy = false;
                state.error = null;
            })
            .addCase(getMonthlySubjectAccuracy.failed, (state, action) => {
                state.isLoadingMonthlyAccuracy = false;
                state.error = action.payload || "Failed to fetch monthly subject accuracy.";
            })
            // Daily accuracy trend cases
            .addCase(getDailyAccuracyTrend.request, (state) => {
                state.isLoadingDailyTrend = true;
                state.error = null;
            })
            .addCase(getDailyAccuracyTrend.success, (state, action) => {
                state.dailyAccuracyTrend = action.payload;
                state.isLoadingDailyTrend = false;
                state.error = null;
            })
            .addCase(getDailyAccuracyTrend.failed, (state, action) => {
                state.isLoadingDailyTrend = false;
                state.error = action.payload || "Failed to fetch daily accuracy trend.";
            });
    },
});

export const { setLoading, clearError, resetState: resetRecordState } = recordSlice.actions;
export default recordSlice.reducer;
